// as-cetra-vet-ui/src/components/authenticated/handout/customise-mode/SaveCustomisedHandoutModal.js

import React, { useState, useEffect, useContext } from 'react';
import {
    Dialog,
    Button,
    TextField,
    Radio,
    RadioGroup,
    FormControlLabel,
    Box,
    Autocomplete,
    FormControl,
    Checkbox,
    CircularProgress,
    IconButton,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { designTokens } from '../../../../theme';
import mixpanel from 'mixpanel-browser';
import StoreContext from '../../../../react-core-lib/components/app/store-context';
import UserApi from '../../../../rest-api/user-api';
import { useSnackbar } from '../../SnackbarProvider';
import BlockingSpinner from '../../../UIControls/BlockingSpinner';
import { useCustomiseMode } from './CustomiseModeContext';
// Phase 2: Import WB save dependencies
import { mergeCanvasWithIllustration } from '../whiteboard/mergeCanvasWithIllustration';
import { handleCreateOrUpdateWhiteboard } from '../whiteboard/WhiteboardService';
import { getDocumentTitle } from '../../../../components/helper/contentOperation';
import DownloadUtils from '../../../../utils/download-utils';
import {
    formatPatientAutocompleteLabel,
    formatPatientAutocompleteSearchText,
    patientAutocompleteNoOptionsText,
    patientAutocompleteSx,
    renderPatientAutocompleteOption,
} from '../../../UIControls/patient-autocomplete-option';
import { CLIENT_NAME_LABEL, PATIENT_NAME_LABEL } from '../../../helper/patientLabels';
import {
    isAuthExpiredDialogVisible,
    isAuthExpiredError,
    subscribeAuthExpired,
} from '../../../../utils/auth-error-utils';

const SaveCustomisedHandoutModal = ({
    open,
    onClose,
    handoutData,
    editedContentData,
    onSaved,
    // Phase 2: Combined mode props
    isCombinedMode = false,
    hasActiveWhiteboardDrawings = false,
    activeCanvasRef = null,
    canvasRef = null,
    sourceCustomisedHandout = null,
    hasCustomisedChanges = false,
}) => {
    const { userDataStore, authStore, customisedHandoutStore, whiteboardStore, imageStore } = useContext(StoreContext);
    const { selectedWhiteboard } = useCustomiseMode();
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveToMyLibrary, setSaveToMyLibrary] = useState(true); // default to My Library
    const showSnackbar = useSnackbar();

    // Patient-related state (for My Clinic)
    const [patientList, setPatientList] = useState([]);
    const [patientListLoading, setPatientListLoading] = useState(false);
    const [patientType, setPatientType] = useState('existing');
    const [selectedPatient, setSelectedPatient] = useState(''); // For existing patient ID
    const [manualPatientName, setManualPatientName] = useState(''); // For new patient entry
    const [clientSurname, setClientSurname] = useState('');
    const [clinicPatientId, setClinicPatientId] = useState('');
    const [includeAdditionalInfo, setIncludeAdditionalInfo] = useState(false);
    const [dob, setDob] = useState('');
    const [breed, setBreed] = useState('');
    const [species, setSpecies] = useState('');
    const [microchip, setMicrochip] = useState('');

    const isStudent = userDataStore?.subscriptionData?.accountType === 'Student';
    const accountType = userDataStore?.subscriptionData?.accountType;
    const sourcePatientId = sourceCustomisedHandout?.patientId || null;
    const isPatientSavedContext = Boolean(sourceCustomisedHandout?.customisedId && sourcePatientId);
    const isMyHandoutsContext = Boolean(sourceCustomisedHandout?.customisedId && !sourcePatientId);

    useEffect(() => {
        if (open) {
            setSaveToMyLibrary(isPatientSavedContext || (!hasCustomisedChanges && isMyHandoutsContext) ? false : true);
            setNotes('');
            setPatientType('existing');
            setSelectedPatient('');
            setManualPatientName('');
            setClientSurname('');
            setClinicPatientId('');
            setIncludeAdditionalInfo(false);
            setDob('');
            setBreed('');
            setSpecies('');
            setMicrochip('');
        }
    }, [open, hasCustomisedChanges, isMyHandoutsContext, isPatientSavedContext]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        if (isAuthExpiredDialogVisible()) {
            setSaving(false);
            onClose();
            return undefined;
        }

        return subscribeAuthExpired(() => {
            setSaving(false);
            onClose();
        });
    }, [open, onClose]);

    useEffect(() => {
        if (['Student', 'Professional'].includes(accountType)) {
            setSaveToMyLibrary(true);
        }
    }, [accountType]);

    useEffect(() => {
        if (!isStudent && open && !saveToMyLibrary) {
            fetchPatientList();
        }
    }, [isStudent, open, saveToMyLibrary]);

    const fetchPatientList = async () => {
        setPatientListLoading(true);
        try {
            const response = await UserApi.getPatientsByFilter(authStore.idToken, {
                filterType: 'patientStatus',
                filterValue: 'active',
            });

            if (response?.patients) {
                setPatientList(response.patients);
            }
        } catch (error) {
            console.error('Failed to fetch patients:', error);
        } finally {
            setPatientListLoading(false);
        }
    };

    const fetchClinicianData = async () => {
        try {
            console.warn('Fetching Clinician data...');
            const clinicianDataResponse = await UserApi.getClinicianData(authStore.idToken);
            return clinicianDataResponse;
        } catch (error) {
            console.error('Failed to fetch clinician data:', error);
            return null;
        }
    };

    const handleSave = async () => {
        if (saving) return;
        setSaving(true);

        // Validate patient selection for My Clinic
        if (!isStudent && !saveToMyLibrary) {
            if (patientType === 'existing' && !selectedPatient) {
                showSnackbar('Please select an existing patient.', 'error');
                setSaving(false);
                return;
            }
            if (patientType === 'new') {
                if (!manualPatientName.trim()) {
                    showSnackbar(`${PATIENT_NAME_LABEL} is required.`, 'error');
                    setSaving(false);
                    return;
                }

                if (!clientSurname.trim()) {
                    showSnackbar(`${CLIENT_NAME_LABEL} is required.`, 'error');
                    setSaving(false);
                    return;
                }
            }
        }

        if (!hasCustomisedChanges && isMyHandoutsContext && saveToMyLibrary) {
            showSnackbar('This customised handout is already saved to My handouts.', 'warning');
            setSaving(false);
            return;
        }

        if (
            !hasCustomisedChanges &&
            isPatientSavedContext &&
            patientType === 'existing' &&
            selectedPatient === sourcePatientId
        ) {
            showSnackbar('Please select a different patient.', 'warning');
            setSaving(false);
            return;
        }

        try {
            console.warn('Fetching clinician data before saving...');
            const clinicianData = await fetchClinicianData();

            if (!clinicianData || !clinicianData.userId) {
                console.error('Error: clinicianData.userId is undefined.');
                if (!isAuthExpiredDialogVisible()) {
                    showSnackbar('Failed to retrieve user data. Please try again.', 'error');
                }
                setSaving(false);
                return;
            }

            // Determine patient names
            let finalPatientName = null;
            let finalClientSurname = null;
            let finalPatientId = null;

            if (!saveToMyLibrary) {
                if (patientType === 'existing') {
                    const existing = patientList.find((p) => p.patientId === selectedPatient);
                    finalPatientName = existing ? existing.patientName : '';
                    finalClientSurname = existing ? existing.clientSurname : '';
                    finalPatientId = selectedPatient;
                } else {
                    // New patient - create patient record first
                    finalPatientName = manualPatientName.trim();
                    finalClientSurname = clientSurname.trim();

                    const newPatientData = {
                        patientName: finalPatientName,
                        clientSurname: finalClientSurname,
                    };

                    if (clinicPatientId) newPatientData.clinicPatientId = clinicPatientId;
                    if (dob) newPatientData.DOB = dob;
                    if (breed) newPatientData.breed = breed;
                    if (species) newPatientData.species = species;
                    if (microchip) newPatientData.microchipNumber = microchip;

                    try {
                        console.warn('Creating a new patient with data:', newPatientData);
                        const patientResp = await UserApi.createOrUpdatePatient(authStore.idToken, newPatientData);

                        if (!patientResp?.patientId) {
                            console.error('Failed to create patient: Unexpected response', patientResp);
                            showSnackbar(
                                'Failed to create patient. The response did not contain a valid patientId.',
                                'error',
                            );
                            setSaving(false);
                            return;
                        }

                        finalPatientId = patientResp.patientId;
                        console.warn(`New patient created with ID: ${finalPatientId}`);
                    } catch (error) {
                        console.error('Error creating patient:', error);
                        showSnackbar('Failed to create patient. Please try again.', 'error');
                        setSaving(false);
                        return;
                    }
                }
            }

            // Replace illustration with whiteboard if selected
            let finalContentData = JSON.parse(JSON.stringify(editedContentData)); // Deep clone
            if (selectedWhiteboard) {
                console.log('[Save CH] Replacing illustration with whiteboard:', selectedWhiteboard.canvasImageLink);
                // Find and replace the first illustration across all sections
                const locale = 'en-UK';
                if (finalContentData[locale]) {
                    let replaced = false;
                    for (const section of finalContentData[locale]) {
                        if (section.blocks) {
                            for (const block of section.blocks) {
                                if (block.type === 'illustration') {
                                    // Replace with whiteboard image path
                                    block.src = selectedWhiteboard.canvasImageLink;
                                    console.log('[Save CH] Replaced illustration src:', block.src);
                                    replaced = true;
                                    break;
                                }
                            }
                        }
                        if (replaced) break;
                    }
                }
            }

            // Prepare payload for save
            // Patient-linked customised handouts are historical patient records. Saving further edits must create
            // a new patient handout instead of overwriting the source record.
            const shouldUpdateSourceHandout =
                hasCustomisedChanges && sourceCustomisedHandout?.customisedId && saveToMyLibrary && isMyHandoutsContext;

            const payload = {
                customisedId: shouldUpdateSourceHandout ? sourceCustomisedHandout.customisedId : null,
                baseContentId: handoutData.contentId,
                baseLastUpdated: handoutData.lastUpdated,
                locale: 'en-UK',
                effectiveContentData: finalContentData,
                attachedWhiteboardId: selectedWhiteboard ? selectedWhiteboard.whiteboardId : null,
                attachedWhiteboardCanvasImageLink: selectedWhiteboard ? selectedWhiteboard.canvasImageLink : null,
                attachedWhiteboardETag: selectedWhiteboard ? selectedWhiteboard.eTag : null,
                notes: notes.trim() || null,
                customisedStatus: 'active',
                updatedById: userDataStore.userId,
                updatedByFirstName: userDataStore.userData.firstName || null,
                updatedByLastName: userDataStore.userData.lastName || null,
                baseContentIdUserIdCompositeKey: `${handoutData.contentId}#${clinicianData.userId}`,
                // Patient fields (for My Clinic)
                patientId: saveToMyLibrary ? null : finalPatientId,
                patientName: saveToMyLibrary ? null : finalPatientName,
                clientSurname: saveToMyLibrary ? null : finalClientSurname,
                microchipNumber: saveToMyLibrary ? null : microchip || null,
                clinicPatientId: saveToMyLibrary ? null : clinicPatientId || null,
            };

            console.warn('Saving customised handout...');
            const response = await UserApi.createOrUpdateCustomisedHandout(authStore.idToken, payload);
            console.warn('Saved customised handout result:', response);

            if (response && response.customisedId) {
                // Backend returns { message, customisedId, customisedStatus }
                // We need to construct the full object for the store

                // Construct full customised handout object for store
                const fullCustomisedHandout = {
                    customisedId: response.customisedId,
                    userId: clinicianData.userId,
                    baseContentId: payload.baseContentId,
                    baseLastUpdated: payload.baseLastUpdated,
                    locale: payload.locale,
                    effectiveContentData: payload.effectiveContentData,
                    attachedWhiteboardId: payload.attachedWhiteboardId,
                    notes: payload.notes,
                    customisedStatus: response.customisedStatus,
                    lastUpdated: Date.now(), // Use timestamp number like backend
                    updatedBy: userDataStore.userData.emailAddress,
                    updatedById: userDataStore.userId,
                    updatedByFirstName: userDataStore.userData.firstName || null,
                    updatedByLastName: userDataStore.userData.lastName || null,
                    baseContentIdUserIdCompositeKey: `${payload.baseContentId}#${clinicianData.userId}`,
                    // Patient fields (for My Clinic)
                    patientId: payload.patientId,
                    patientName: payload.patientName,
                    clientSurname: payload.clientSurname,
                    microchipNumber: payload.microchipNumber,
                    clinicPatientId: payload.clinicPatientId,
                };

                localStorage.setItem(
                    'lastCustomisedHandoutState',
                    JSON.stringify({
                        customisedId: response.customisedId,
                        baseContentId: payload.baseContentId,
                        userId: clinicianData.userId,
                        updatedByFirstName: payload.updatedByFirstName || null,
                        updatedByLastName: payload.updatedByLastName || null,
                        patientName: payload.patientName || null,
                        clientSurname: payload.clientSurname || null,
                        patientId: payload.patientId || null,
                        microchipNumber: payload.microchipNumber || null,
                        clinicPatientId: payload.clinicPatientId || null,
                    }),
                );

                // Add/update in CustomisedHandoutStore
                if (customisedHandoutStore) {
                    await customisedHandoutStore.addOrUpdateCustomisedHandout(fullCustomisedHandout);
                    console.log('Customised handout added to store:', response.customisedId);
                }

                // Update userDataStore for My Library
                // Note: Students should never see this feature (Professional/Clinic only)
                if (saveToMyLibrary) {
                    const currentCustomisedHandouts = userDataStore.userData.customisedHandouts || [];
                    console.log('Current customisedHandouts in userDataStore:', currentCustomisedHandouts);
                    console.log('Saving to My Library, saveToMyLibrary:', saveToMyLibrary);

                    // Optional: enforce the 300 limit (same as whiteboards)
                    if (currentCustomisedHandouts.length >= 300) {
                        showSnackbar(
                            'You have reached the maximum number of customised handouts (300) in My Library.',
                            'warning',
                        );
                        setSaving(false);
                        return;
                    }

                    // Check if already exists in array
                    if (!currentCustomisedHandouts.includes(response.customisedId)) {
                        const updatedCustomisedHandouts = [...currentCustomisedHandouts, response.customisedId];
                        console.log('Updated customisedHandouts array:', updatedCustomisedHandouts);

                        // Update userDataStore locally
                        userDataStore.setUserData({
                            ...userDataStore.userData,
                            customisedHandouts: updatedCustomisedHandouts,
                        });

                        // Sync with backend
                        const isMultiUser = userDataStore?.subscriptionData?.accountType?.includes('multiuser');
                        console.log('Is multiuser account:', isMultiUser);
                        console.log('Account type:', userDataStore?.subscriptionData?.accountType);
                        console.log('User data:', {
                            emailAddress: userDataStore.userData.emailAddress,
                            inviteId: userDataStore.userData.inviteId,
                        });

                        const updatePayload = {
                            customisedHandouts: updatedCustomisedHandouts,
                        };

                        try {
                            if (isMultiUser) {
                                updatePayload.action = 'updateMultiuser';
                                updatePayload.emailAddress = userDataStore.userData.emailAddress;
                                updatePayload.inviteId = userDataStore.userData.inviteId;
                                console.log('Calling updateClinicianDataMultiuser with payload:', updatePayload);
                                const result = await UserApi.updateClinicianDataMultiuser(
                                    authStore.idToken,
                                    updatePayload,
                                );
                                console.log('updateClinicianDataMultiuser result:', result);
                            } else {
                                console.log('Calling updateClinicianData with payload:', updatePayload);
                                const result = await UserApi.updateClinicianData(authStore.idToken, updatePayload);
                                console.log('updateClinicianData result:', result);
                            }
                            console.log('Successfully synced customisedHandouts with backend');
                        } catch (err) {
                            console.error('Failed to sync customisedHandouts with backend', err);
                            showSnackbar('Failed to sync with backend. Please refresh the page.', 'warning');
                        }
                    } else {
                        console.log('Customised handout already exists in array, skipping backend update');
                    }
                }

                // Phase 2: Combined mode - save whiteboard if active
                let whiteboardResponse = null;
                let savedWhiteboard = null;
                if (isCombinedMode && hasActiveWhiteboardDrawings && canvasRef?.current) {
                    console.warn('[Combined Mode] Saving whiteboard with customised handout...');
                    try {
                        // Merge canvas with illustration
                        const mergedBlob = await mergeCanvasWithIllustration(canvasRef);

                        // Get canvas JSON
                        const rawJson = canvasRef.current.toJSON();
                        rawJson.canvasWidth = canvasRef.current.getWidth();
                        rawJson.canvasHeight = canvasRef.current.getHeight();
                        const canvasJson = JSON.stringify(rawJson);

                        // Generate filename
                        const date = new Date();
                        const formattedDateTime = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}_${String(date.getHours()).padStart(2, '0')}${String(date.getMinutes()).padStart(2, '0')}${String(date.getSeconds()).padStart(2, '0')}`;
                        const handoutShortName = handoutData.contentName.split(' ').slice(0, 3).join('');
                        const fileName = `${formattedDateTime}_${handoutShortName}`;

                        // Check if diagram handout
                        const locale = 'en-UK';
                        const isDiagramHandout = handoutData?.contentData?.[locale]?.some(
                            (section) => section.sectionName === 'diagram',
                        );
                        const diagramVariant = isDiagramHandout
                            ? handoutData.showLabels
                                ? 'withLabels'
                                : 'noLabels'
                            : null;

                        // Prepare WB save data
                        const wbSaveData = {
                            userId: clinicianData.userId,
                            patientId: saveToMyLibrary ? null : finalPatientId,
                            patientName: finalPatientName,
                            clientSurname: finalClientSurname,
                            clinicPatientId,
                            dob,
                            breed,
                            species,
                            microchip,
                            notes,
                            fileName,
                            canvasData: canvasJson,
                            mergedImageBlob: mergedBlob,
                            handoutId: handoutData.contentId,
                            diagramVariant,
                            handoutContentName: handoutData.contentName,
                            handoutAlphabeticalName: handoutData.alphabeticalName,
                            handoutDocumentTitle: getDocumentTitle(handoutData.contentData, locale),
                            whiteboardStatus: 'active',
                            myLibrary: saveToMyLibrary,
                            updatedById: userDataStore.userId,
                            updatedByFirstName: userDataStore.userData.firstName || null,
                            updatedByLastName: userDataStore.userData.lastName || null,
                            lastUsedColours: null,
                        };

                        // Save whiteboard
                        whiteboardResponse = await handleCreateOrUpdateWhiteboard(
                            authStore.idToken,
                            wbSaveData,
                            showSnackbar,
                        );
                        console.warn('[Combined Mode] Whiteboard saved:', whiteboardResponse);

                        // Update whiteboard store
                        if (whiteboardResponse) {
                            const newWhiteboard = {
                                whiteboardId: whiteboardResponse.whiteboardId,
                                canvasImageLink: whiteboardResponse.canvasImageLink,
                                canvasJsonLink: whiteboardResponse.canvasJsonLink,
                                eTag: whiteboardResponse.eTag || null,
                                fileName,
                                handoutId: handoutData.contentId,
                                diagramVariant,
                                handoutContentName: handoutData.contentName,
                                handoutAlphabeticalName: handoutData.alphabeticalName,
                                handoutDocumentTitle: getDocumentTitle(handoutData.contentData, locale),
                                notes,
                                whiteboardStatus: 'active',
                                userId: clinicianData.userId,
                                lastUsedColours: null,
                                patientId: saveToMyLibrary ? null : finalPatientId,
                                patientName: saveToMyLibrary ? null : finalPatientName,
                                clientSurname: saveToMyLibrary ? null : finalClientSurname,
                                clinicPatientId: saveToMyLibrary ? null : clinicPatientId || null,
                                microchipNumber: saveToMyLibrary ? null : microchip || null,
                                handoutIdUserIdCompositeKey: `${handoutData.contentId}#${clinicianData.userId}`,
                                lastUpdated: new Date().toISOString(),
                                updatedById: userDataStore.userId,
                                updatedByFirstName: userDataStore.userData.firstName || null,
                                updatedByLastName: userDataStore.userData.lastName || null,
                            };

                            await whiteboardStore.setWhiteboardData([newWhiteboard]);
                            savedWhiteboard = newWhiteboard;

                            localStorage.setItem(
                                'lastWhiteboardState',
                                JSON.stringify({
                                    whiteboardId: whiteboardResponse.whiteboardId,
                                    handoutId: handoutData.contentId,
                                    canvasImageLink: whiteboardResponse.canvasImageLink,
                                    canvasJsonLink: whiteboardResponse.canvasJsonLink,
                                    fileName,
                                    eTag: whiteboardResponse.eTag || null,
                                    diagramVariant,
                                    updatedByFirstName: userDataStore.userData.firstName || null,
                                    updatedByLastName: userDataStore.userData.lastName || null,
                                    patientId: saveToMyLibrary ? null : finalPatientId,
                                    patientName: saveToMyLibrary ? null : finalPatientName,
                                    clientSurname: saveToMyLibrary ? null : finalClientSurname,
                                    clinicPatientId: saveToMyLibrary ? null : clinicPatientId || null,
                                    microchipNumber: saveToMyLibrary ? null : microchip || null,
                                }),
                            );

                            // Update userDataStore for My Library
                            if (saveToMyLibrary && whiteboardResponse.whiteboardId) {
                                const currentWhiteboards = userDataStore.userData.whiteboards || [];
                                if (currentWhiteboards.length < 300) {
                                    const updatedWhiteboards = [...currentWhiteboards, whiteboardResponse.whiteboardId];
                                    userDataStore.setUserData({
                                        ...userDataStore.userData,
                                        whiteboards: updatedWhiteboards,
                                    });

                                    // Sync with backend
                                    const isMultiUser =
                                        userDataStore?.subscriptionData?.accountType?.includes('multiuser');
                                    const wbPayload = { whiteboards: updatedWhiteboards };

                                    try {
                                        if (isMultiUser) {
                                            wbPayload.action = 'updateMultiuser';
                                            wbPayload.emailAddress = userDataStore.userData.emailAddress;
                                            wbPayload.inviteId = userDataStore.userData.inviteId;
                                            await UserApi.updateClinicianDataMultiuser(authStore.idToken, wbPayload);
                                        } else {
                                            await UserApi.updateClinicianData(authStore.idToken, wbPayload);
                                        }
                                    } catch (err) {
                                        console.error('[Combined Mode] Failed to sync whiteboards with backend', err);
                                    }
                                }
                            }

                            // Save image to IndexedDB
                            if (whiteboardResponse.canvasImageLink) {
                                const imageExists = await imageStore.getImageFromIndexedDB(
                                    whiteboardResponse.canvasImageLink,
                                );
                                if (!imageExists) {
                                    const payload = {
                                        files: [{ method: 'get', key: whiteboardResponse.canvasImageLink }],
                                        contentCategory: 'whiteboard',
                                    };
                                    const resp = await UserApi.generatePresignedUrls(authStore.idToken, payload);
                                    const presignedUrl = resp.getUrls[0].presignedUrl;
                                    const downloadResult =
                                        await DownloadUtils.downloadImageFromPresignedUrl(presignedUrl);
                                    await imageStore.saveImageToIndexedDB(
                                        whiteboardResponse.canvasImageLink,
                                        downloadResult.base64data,
                                        whiteboardResponse.eTag || null,
                                    );
                                }
                            }

                            // Update CH record with attached whiteboard
                            const updatedCHPayload = {
                                ...payload,
                                customisedId: response.customisedId,
                                attachedWhiteboardId: whiteboardResponse.whiteboardId,
                                attachedWhiteboardCanvasImageLink: whiteboardResponse.canvasImageLink,
                                attachedWhiteboardETag: whiteboardResponse.eTag || null,
                            };

                            // Update effectiveContentData to replace illustration with new WB
                            const updatedContentData = JSON.parse(JSON.stringify(finalContentData));
                            if (updatedContentData[locale]) {
                                let replaced = false;
                                for (const section of updatedContentData[locale]) {
                                    if (section.blocks) {
                                        for (const block of section.blocks) {
                                            if (block.type === 'illustration') {
                                                block.src = whiteboardResponse.canvasImageLink;
                                                console.log(
                                                    '[Combined Mode] Updated CH illustration with new WB:',
                                                    block.src,
                                                );
                                                replaced = true;
                                                break;
                                            }
                                        }
                                    }
                                    if (replaced) break;
                                }
                            }
                            updatedCHPayload.effectiveContentData = updatedContentData;

                            console.warn('[Combined Mode] Updating CH with attached whiteboard...');
                            await UserApi.createOrUpdateCustomisedHandout(authStore.idToken, updatedCHPayload);

                            // Update local store
                            fullCustomisedHandout.attachedWhiteboardId = whiteboardResponse.whiteboardId;
                            fullCustomisedHandout.attachedWhiteboardCanvasImageLink =
                                whiteboardResponse.canvasImageLink;
                            fullCustomisedHandout.attachedWhiteboardETag = whiteboardResponse.eTag || null;
                            fullCustomisedHandout.effectiveContentData = updatedContentData;
                            if (customisedHandoutStore) {
                                await customisedHandoutStore.addOrUpdateCustomisedHandout(fullCustomisedHandout);
                            }
                        }
                    } catch (error) {
                        console.error('[Combined Mode] Failed to save whiteboard:', error);
                        if (!isAuthExpiredError(error) && !isAuthExpiredDialogVisible()) {
                            showSnackbar(
                                'Customised handout saved, but whiteboard save failed. Please try again.',
                                'warning',
                            );
                        }
                    }
                }

                // Track customised handout save
                mixpanel.track('CustomisedHandoutSaved', {
                    email: userDataStore?.userData?.emailAddress,
                    handoutId: handoutData?.contentId,
                    handoutTitle: handoutData?.contentName,
                    saveLocation: saveToMyLibrary ? 'My Library' : 'My Clinic',
                    includesWhiteboard: isCombinedMode && !!whiteboardResponse,
                });

                showSnackbar(
                    isCombinedMode && whiteboardResponse
                        ? 'Customised handout and whiteboard successfully saved!'
                        : 'Customised handout successfully saved!',
                    'success',
                );

                if (onSaved) onSaved(savedWhiteboard);
                else onClose();
            } else {
                if (!isAuthExpiredDialogVisible()) {
                    showSnackbar('Failed to save customised handout. Please try again.', 'error');
                }
            }
        } catch (error) {
            console.error('Error saving customised handout:', error);
            if (!isAuthExpiredError(error) && !isAuthExpiredDialogVisible()) {
                showSnackbar('Failed to save customised handout. Please try again.', 'error');
            }
        } finally {
            setSaving(false);
        }
    };

    const isBlocked = isAuthExpiredDialogVisible();

    const radioLabelSx = {
        '& .MuiFormControlLabel-label': {
            fontFamily: 'Inter, sans-serif',
            fontSize: { xs: 14, sm: 18 },
            lineHeight: { xs: '14px', sm: '18px' },
            letterSpacing: { xs: '-0.56px', sm: '-0.72px' },
            color: designTokens.colors.textSecondary,
        },
    };

    return (
        <Dialog
            open={open && !isBlocked}
            onClose={onClose}
            PaperProps={{
                sx: {
                    borderRadius: '30px !important',
                    padding: { xs: '30px 20px', sm: '30px 28px' },
                    width: { xs: 'calc(100% - 32px)', sm: '711px' },
                    maxWidth: { xs: 'calc(100% - 32px)', sm: '711px' },
                    maxHeight: 'calc(100dvh - 32px)',
                    boxSizing: 'border-box',
                    overflowY: 'auto',
                },
            }}
            maxWidth={false}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: { xs: '8px', sm: '24px' },
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 600,
                            fontSize: { xs: 26, sm: 32 },
                            lineHeight: { xs: '26px', sm: '32px' },
                            letterSpacing: { xs: '-1.04px', sm: '-1.28px' },
                            color: designTokens.colors.textPrimary,
                            pr: '44px',
                        }}
                    >
                        Save customised handout
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            p: 0,
                            width: { xs: '28px', sm: '36px' },
                            height: { xs: '28px', sm: '36px' },
                            background: 'rgb(246, 246, 246)',
                            borderRadius: { xs: '11.928px', sm: '50%' },
                            flexShrink: 0,
                        }}
                    >
                        <CloseIcon sx={{ width: { xs: '16px', sm: '24px' }, height: { xs: '16px', sm: '24px' } }} />
                    </IconButton>
                </Box>
                {saving && <BlockingSpinner open={true} />}
                {!isStudent && (
                    <>
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: 'Inter, sans-serif',
                                    fontSize: { xs: 14, sm: 18 },
                                    lineHeight: { xs: '14px', sm: '18px' },
                                    letterSpacing: { xs: '-0.56px', sm: '-0.72px' },
                                    color: designTokens.colors.textSecondary,
                                    mb: { xs: '12px', sm: '16px' },
                                }}
                            >
                                Please select where you&apos;d like to save your handout.
                            </Typography>
                            <RadioGroup
                                row
                                value={saveToMyLibrary ? 'library' : 'clinic'}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setSaveToMyLibrary(val === 'library');
                                }}
                            >
                                <FormControlLabel
                                    value="library"
                                    control={<Radio sx={{ color: '#FF669B', '&.Mui-checked': { color: '#FF669B' } }} />}
                                    label="Save to My handouts"
                                    disabled={!hasCustomisedChanges && isMyHandoutsContext}
                                    sx={radioLabelSx}
                                />
                                <FormControlLabel
                                    value="clinic"
                                    control={<Radio sx={{ color: '#FF669B', '&.Mui-checked': { color: '#FF669B' } }} />}
                                    label="Save to a patient record"
                                    sx={radioLabelSx}
                                />
                            </RadioGroup>
                            {!hasCustomisedChanges && isMyHandoutsContext && (
                                <Box sx={{ fontSize: '0.875rem', color: 'rgba(32, 31, 32, 0.4)', mt: 0.5 }}>
                                    *This customised handout is already saved to My handouts. You can still save it to a
                                    patient record.
                                </Box>
                            )}
                        </Box>

                        {!saveToMyLibrary && (
                            <>
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{ mt: 2, mb: 2 }}
                                >
                                    <RadioGroup
                                        row
                                        value={patientType}
                                        onChange={(e) => {
                                            setPatientType(e.target.value);
                                            if (e.target.value === 'new') {
                                                setSelectedPatient('');
                                                setClinicPatientId('');
                                                setDob('');
                                                setBreed('');
                                                setSpecies('');
                                                setMicrochip('');
                                            } else {
                                                setManualPatientName('');
                                                setClientSurname('');
                                            }
                                        }}
                                    >
                                        <FormControlLabel
                                            value="existing"
                                            control={
                                                <Radio
                                                    sx={{ color: '#FF669B', '&.Mui-checked': { color: '#FF669B' } }}
                                                />
                                            }
                                            label="Existing Patient"
                                            sx={radioLabelSx}
                                        />
                                        <FormControlLabel
                                            value="new"
                                            control={
                                                <Radio
                                                    sx={{ color: '#FF669B', '&.Mui-checked': { color: '#FF669B' } }}
                                                />
                                            }
                                            label="New Patient"
                                            sx={radioLabelSx}
                                        />
                                    </RadioGroup>
                                </Box>

                                {patientType === 'existing' && (
                                    <FormControl fullWidth margin="normal">
                                        <Autocomplete
                                            fullWidth
                                            options={patientList}
                                            loading={patientListLoading}
                                            loadingText="Loading patients..."
                                            getOptionDisabled={(option) =>
                                                !hasCustomisedChanges &&
                                                isPatientSavedContext &&
                                                option.patientId === sourcePatientId
                                            }
                                            getOptionLabel={(option) =>
                                                formatPatientAutocompleteLabel(
                                                    typeof option === 'string' ? { patientName: option } : option,
                                                )
                                            }
                                            value={patientList.find((p) => p.patientId === selectedPatient) || null}
                                            onChange={(e, newValue) => {
                                                if (newValue) {
                                                    setSelectedPatient(newValue.patientId);
                                                    setClinicPatientId(newValue.clinicPatientId || '');
                                                    setDob(newValue.DOB || '');
                                                    setBreed(newValue.breed || '');
                                                    setSpecies(newValue.species || '');
                                                    setMicrochip(newValue.microchipNumber || '');
                                                } else {
                                                    setSelectedPatient('');
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Select patient"
                                                    margin="normal"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {patientListLoading && (
                                                                    <CircularProgress color="inherit" size={18} />
                                                                )}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                    inputProps={{ ...params.inputProps, className: 'mp-mask-patient' }}
                                                />
                                            )}
                                            noOptionsText={patientAutocompleteNoOptionsText}
                                            renderOption={renderPatientAutocompleteOption}
                                            filterOptions={(options, { inputValue }) =>
                                                options.filter((patient) => {
                                                    const query = inputValue.toLowerCase();
                                                    return formatPatientAutocompleteSearchText(patient).includes(query);
                                                })
                                            }
                                            sx={patientAutocompleteSx}
                                        />
                                    </FormControl>
                                )}

                                {patientType === 'new' && (
                                    <>
                                        <TextField
                                            label={PATIENT_NAME_LABEL}
                                            fullWidth
                                            margin="normal"
                                            value={manualPatientName}
                                            onChange={(e) => setManualPatientName(e.target.value)}
                                            required
                                            inputProps={{ className: 'mp-mask-patient' }}
                                        />
                                        <TextField
                                            label={CLIENT_NAME_LABEL}
                                            fullWidth
                                            margin="normal"
                                            value={clientSurname}
                                            onChange={(e) => setClientSurname(e.target.value)}
                                            required
                                            inputProps={{ className: 'mp-mask-patient' }}
                                        />
                                    </>
                                )}

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={includeAdditionalInfo}
                                            onChange={(e) => setIncludeAdditionalInfo(e.target.checked)}
                                        />
                                    }
                                    label={
                                        patientType === 'existing'
                                            ? 'Show patient details'
                                            : 'Add additional patient details'
                                    }
                                    sx={radioLabelSx}
                                />
                                {includeAdditionalInfo && (
                                    <>
                                        <TextField
                                            label="Clinic Patient ID"
                                            fullWidth
                                            margin="normal"
                                            value={clinicPatientId}
                                            onChange={(e) => setClinicPatientId(e.target.value)}
                                            disabled={patientType === 'existing'}
                                            inputProps={{ className: 'mp-mask-patient' }}
                                        />
                                        <TextField
                                            label="Date of Birth"
                                            type="date"
                                            fullWidth
                                            margin="normal"
                                            value={dob}
                                            onChange={(e) => setDob(e.target.value)}
                                            inputProps={{
                                                max: new Date().toISOString().split('T')[0],
                                                className: 'mp-mask-patient',
                                            }}
                                            InputLabelProps={{ shrink: true }}
                                            disabled={patientType === 'existing'}
                                        />
                                        <TextField
                                            label="Species"
                                            fullWidth
                                            margin="normal"
                                            value={species}
                                            onChange={(e) => setSpecies(e.target.value)}
                                            disabled={patientType === 'existing'}
                                        />
                                        <TextField
                                            label="Breed"
                                            fullWidth
                                            margin="normal"
                                            value={breed}
                                            onChange={(e) => setBreed(e.target.value)}
                                            disabled={patientType === 'existing'}
                                        />
                                        <TextField
                                            label="Microchip Number"
                                            fullWidth
                                            margin="normal"
                                            value={microchip}
                                            onChange={(e) => setMicrochip(e.target.value)}
                                            disabled={patientType === 'existing'}
                                            inputProps={{ className: 'mp-mask-patient' }}
                                        />
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}

                <TextField
                    label="Notes"
                    fullWidth
                    multiline
                    rows={3}
                    margin="normal"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    inputProps={{ className: 'mp-mask-patient' }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: '24px', sm: '28px' } }}>
                    <Button
                        onClick={handleSave}
                        disabled={saving}
                        sx={{
                            backgroundColor: '#FF669B',
                            color: '#FFFFFF',
                            borderRadius: { xs: '100px', sm: 36 },
                            width: { xs: '100%', sm: '160px' },
                            height: { xs: '35px', sm: '44px' },
                            fontSize: { xs: 14, sm: 16 },
                            fontWeight: 500,
                            lineHeight: { xs: '14px', sm: '16px' },
                            letterSpacing: { xs: '-0.28px', sm: '-0.64px' },
                            textTransform: 'none',
                            '&:hover': { backgroundColor: '#e65588' },
                            '&.Mui-disabled': { backgroundColor: 'rgba(255, 102, 155, 0.5)', color: '#FFFFFF' },
                        }}
                    >
                        Save
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};

export default SaveCustomisedHandoutModal;
